import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import soundwaves from '../assets/images/soundwaves.png';
import AudioPlayer from '../components/audio-player';
import constants from '../constants';
import { useTestState } from '../context/TestContext';
import useTest from '../hooks/useTest';
import {
  algorithmValidationSequences,
  type SequenceOption,
} from '../algorithmValidationSequences';
import {
  computeNextDbWebAligned,
  sequenceUsesBigMissJump,
} from '../utils/hearingLoudnessStep';

const audioDuration = 1738; // in ms

// this feels dumb, gotta be a better way
type IndexMap = {
  500: '500 Hz';
  1000: '1000 Hz';
  2000: '2000 Hz';
  3000: '3000 Hz';
  4000: '4000 Hz';
  8000: '8000 Hz';
};

const indexMap: IndexMap = {
  500: '500 Hz',
  1000: '1000 Hz',
  2000: '2000 Hz',
  3000: '3000 Hz',
  4000: '4000 Hz',
  8000: '8000 Hz',
};

const sequenceOptions = algorithmValidationSequences;

const Test = ({ isB2b2c, isB2b }: { isB2b2c: boolean; isB2b: boolean }) => {
  const sequenceModeEnabled = constants.localHearingOnly;
  // INIT USETEST HOOK
  const {
    testData,
    updateTestData,
    resetTestData,
    loudness,
    setLoudness,
    frequency,
    setFrequency,
    isTraining,
    isRetest,
    checkStoppingCriteria,
    handleEndTest,
    dataPosted,
    setDataPosted,
    freqList,
  } = useTest({
    isB2b2c,
    isB2b,
    algorithmValidationOnly: sequenceModeEnabled,
  });

  const isMobile = window.innerWidth <= 620;
  const navigate = useNavigate();

  const [hasPressedHeardOnce, setHasPressedHeardOnce] = useState(false);
  const [heardPressed, setHeardPressed] = useState(false);
  const [heardAnimation, setHeardAnimation] = useState(false);

  // value is time since audio began to button click
  const [heardInterval, setHeardInterval] = useState<number | null>(null);
  const [timer, setTimer] = useState(0);
  const [trialIndex, setTrialIndex] = useState(0);
  const [trialRunning, setTrialRunning] = useState(false);

  const [testStarted, setTestStarted] = useState(false);
  const [testEnded, setTestEnded] = useState(false);

  const [backEventFinished, setBackEventFinished] = useState(false);

  const [retestStarted, setRetestStarted] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<SequenceOption | null>(
    null,
  );
  const [sequenceStepIndex, setSequenceStepIndex] = useState(0);
  const [sequenceError, setSequenceError] = useState('');
  const [sequenceThreshold, setSequenceThreshold] = useState<number | null>(null);

  // refs to access current state values inside trial timeout function
  const heardRef = useRef<boolean | null>(null);
  const heardIntervalRef = useRef<number | null>(null);
  const hasPressedHeardOnceRef = useRef<boolean | null>(null);

  const { state, dispatch } = useTestState();

  const { t, i18n } = useTranslation('test');
  const currentLanguage = i18n.language;
  const localHearingOnly = constants.localHearingOnly;

  // handle back button and refresh events
  useEffect(() => {
    if (!state.contactid) {
      if (localHearingOnly) {
        dispatch({
          type: 'UPDATE_DEMOGRAPHICS',
          payload: {
            contactid: 'LOCAL_CONTACT',
            patientid: 'LOCAL_PATIENT',
            email: 'local-dev@audicus.test',
            nameonresult: 'Local Dev User',
            dob: '1990-01-01',
          },
        });
        dispatch({ type: 'UPDATE_FACILITYID', payload: 'LOCAL_FACILITY' });
        dispatch({ type: 'UPDATE_HEADPHONES_CHECK_PASSED', payload: true });
      } else {
        return navigate('/demographics');
      }
    } else if (!localHearingOnly && !state.headphonesCheckPassed) {
      return navigate('/headphones');
    }

    // show popup when user tries to reload page during test
    const onRefreshEvent = (e: Event) => {
      e.preventDefault();
      return '';
    };

    // show popup when user tries to press back button during test
    const onBackButtonEvent = (e: Event) => {
      e.preventDefault();
      if (!backEventFinished) {
        if (
          window.confirm(
            "Do you want to go back? If you go back, you'll have to restart the test.",
          )
        ) {
          setBackEventFinished(true);
          navigate('/demographics');
        } else {
          window.history.pushState(null, '', window.location.pathname);
          setBackEventFinished(false);
        }
      }
    };
    window.history.pushState(null, '', window.location.pathname);

    window.addEventListener('beforeunload', onRefreshEvent);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('beforeunload', onRefreshEvent);
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, [
    backEventFinished,
    dispatch,
    localHearingOnly,
    navigate,
    state.contactid,
    state.headphonesCheckPassed,
  ]);

  // generates a random, uniform integer on the interval [lower, upper]
  const generateRandomDelay = () => {
    // Generate a random number between 0 and 1
    const randomFraction = Math.random();

    // Scale and shift the random number to the [intervalLower, intervalUpper] region
    const randomNumber =
      constants['intervalLower'] +
      randomFraction *
        (constants['intervalUpper'] - constants['intervalLower']);

    // return to the nearest millisecond
    return Math.round(randomNumber);
  };

  const getRetestTimeString = () => {
    if (Math.floor(0.75 * freqList.length) < 1) {
      return 'less than 1 minute';
    } else if (Math.floor(0.75 * freqList.length) === 1) {
      return `${Math.floor(0.75 * freqList.length)} minute`;
    } else {
      return `${Math.floor(0.75 * freqList.length)} minutes`;
    }
  };

  // creates & recreates test interval until max trial index is reached
  useEffect(() => {
    // set heardPressed and heardInterval to ref so
    // they are accessible inside async timeout function
    heardRef.current = heardPressed;
    heardIntervalRef.current = heardInterval ?? null;
    hasPressedHeardOnceRef.current = hasPressedHeardOnce;

    if (testStarted && !trialRunning && !testEnded) {
      setFrequency(freqList[0]);

      // initialize random delay between tones
      let randDelay = 0;
      if (!hasPressedHeardOnceRef.current) {
        // just do 2 sec between tones to climb up until 'Heard' response is recorded
        randDelay = 2000;
      } else {
        // they have pressed 'Heard' so generate a random delay
        randDelay = generateRandomDelay();
      }
      // add the audio duration to define the entire response interval
      const audioInterval = randDelay + audioDuration;

      // trialRunning avoids multiple timeouts at same time
      setTrialRunning(true);

      // create timeout with random interval
      setTimeout(() => {
        const actualResponse = !!heardRef.current;
        let nextSequenceStep = sequenceStepIndex;

        if (sequenceModeEnabled && selectedSequence) {
          const expectedResponse =
            selectedSequence.expectedResponses[sequenceStepIndex];
          if (typeof expectedResponse !== 'boolean') {
            resetSequenceForCurrentFrequency(
              'Please try again - you did not follow the test sequence properly.',
            );
            return;
          }

          if (actualResponse !== expectedResponse) {
            resetSequenceForCurrentFrequency(
              'Please try again - you did not follow the test sequence properly.',
            );
            return;
          }

          nextSequenceStep += 1;
          setSequenceStepIndex(nextSequenceStep);
        }

        const heard = !!heardRef.current;
        const trialHistory = [
          ...testData.map((row: { loudness: number | string; didUserRespond: boolean }) => ({
            loudness:
              typeof row.loudness === 'number'
                ? row.loudness
                : parseInt(String(row.loudness), 10),
            didUserRespond: row.didUserRespond,
          })),
          { loudness, didUserRespond: heard },
        ];

        if ((!heard && loudness <= 80) || (heard && loudness >= 10)) {
          setLoudness(
            computeNextDbWebAligned(
              loudness,
              heard,
              hasPressedHeardOnceRef.current ?? false,
              trialHistory,
            ),
          );
        }
        if (heard && loudness >= 10) {
          setHeardPressed(false);
        }

        const updateData = {
          trialIndex,
          loudness,
          frequency,
          interval: audioInterval, // store the full interval including stimulus duration
          responseTime: heardIntervalRef.current || NaN,
          didUserRespond: heardRef.current ? true : false,
        };

        // ensure checkStoppingCriteria gets the latest data
        const threshold = checkStoppingCriteria([...testData, updateData]);
        if (
          sequenceModeEnabled &&
          selectedSequence &&
          typeof threshold === 'number' &&
          nextSequenceStep !== selectedSequence.expectedResponses.length
        ) {
          resetSequenceForCurrentFrequency(
            'Please try again - you did not follow the test sequence properly.',
          );
          return;
        }

        if (typeof threshold === 'number') {
          setSequenceThreshold(threshold);
          setTestEnded(true);
        }

        // record test data
        updateTestData(updateData);
        setTrialIndex(trialIndex + 1);
        setTrialRunning(false);
        setTimer(0);
        setHeardInterval(null);
        setHeardPressed(false);
      }, audioInterval);
    }

    if (testEnded && !dataPosted) {
      handleEndTest();
    }
  }, [
    testStarted,
    trialRunning,
    heardPressed,
    heardInterval,
    loudness,
    frequency,
    trialIndex,
    updateTestData,
    testData,
    resetTestData,
    handleEndTest,
    checkStoppingCriteria,
    dataPosted,
    testEnded,
    hasPressedHeardOnce,
    setFrequency,
    setLoudness,
    freqList,
    sequenceModeEnabled,
    sequenceStepIndex,
    selectedSequence,
  ]);

  useEffect(() => {
    if (dataPosted) {
      setSelectedSequence(null);
      setSequenceStepIndex(0);
    }
  }, [dataPosted]);

  // adds hotkey for 'enter' key press to press heard button
  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (event.key === ' ') {
  //       const button = document.getElementById('heardButton')
  //       if (button) {
  //         button.click()
  //       }
  //     }
  //   }

  //   window.addEventListener('keydown', handleKeyDown)

  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown)
  //   }
  // }, [])

  // handle timer for recording how quickly user presses heard button
  useEffect(() => {
    let interval = undefined;
    if (trialRunning) {
      interval = setInterval(() => {
        setTimer((time) => time + 10);
      }, 10);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [trialRunning]);

  const handleClickStartTest = () => {
    setTrialIndex(0);

    // use starting loudness of 45
    setLoudness(45);
    setHasPressedHeardOnce(false);
    setFrequency(freqList[0]);
    setDataPosted(false);
    setTestEnded(false);
    setTestStarted(false);

    // only increment the progress bar if training is done
    if (freqList.length < 12) {
      dispatch({
        type: 'UPDATE_PROGRESS',
        payload: state.progress + 16.66666667,
      });
    }
  };

  const resetSequenceForCurrentFrequency = (message?: string) => {
    resetTestData();
    setTrialIndex(0);
    setLoudness(45);
    setHasPressedHeardOnce(false);
    setFrequency(freqList[0]);
    setDataPosted(false);
    setTestEnded(false);
    setTestStarted(false);
    setTrialRunning(false);
    setTimer(0);
    setHeardInterval(null);
    setHeardPressed(false);
    setSelectedSequence(null);
    setSequenceStepIndex(0);
    if (message) {
      setSequenceError(message);
    }
  };

  const startSequence = (sequence: SequenceOption) => {
    setSequenceError('');
    setSequenceThreshold(null);
    handleClickStartTest();
    setSelectedSequence(sequence);
    setSequenceStepIndex(0);
    setTestStarted(true);
  };

  const handleClickStartRetest = () => {
    setTrialIndex(0);
    // use starting loudness of 45
    setLoudness(45);
    setHasPressedHeardOnce(false);
    setFrequency(freqList[0]);
    setDataPosted(false);
    setTestEnded(false);
    setTestStarted(false);
    setRetestStarted(true);
  };

  const handleClickHeard = () => {
    // if heard is pressed under 100ms after audio begins, do not record
    if (timer >= 100) {
      if (!heardPressed) {
        setHeardInterval(timer);
      }

      setHasPressedHeardOnce(true);
      setHeardPressed(true);
      setHeardAnimation(true);
      setTimeout(() => setHeardAnimation(false), 1000);
    }
  };

  return (
    <div className="test-container">
      <div className="top-title-text-container">
        {isRetest && !retestStarted ? (
          <h1 className="top-title-text">{t('retest-intro')}</h1>
        ) : !dataPosted && !trialRunning ? (
          <h1 className="top-title-text">
            {t('test-intro', {
              ear: frequency[0] === 'R' ? t('right') : t('left'),
            })}{' '}
            <strong>
              {t('ear-label', {
                ear: frequency[0] === 'R' ? t('right') : t('left'),
              })}{' '}
            </strong>
            {!isMobile && currentLanguage !== 'es' && <br />}
            {t('test-instruction')}
          </h1>
        ) : !dataPosted ? (
          <h1 className="top-title-text">
            {t('click-heard', {
              ear: frequency[0] === 'R' ? t('right') : t('left'),
            })}{' '}
            {/* <strong>
              {t('ear-label', {
                ear: frequency[0] === 'R' ? t('right') : t('left'),
              })}{' '}
            </strong> */}
            {!isMobile && <br />}
            {t('uncertain-instruction')}
          </h1>
        ) : freqList.length < 12 ? (
          <h1 className="top-title-text">
            {t('next-frequency')} {!isMobile && <br />}
            {t('test-repetitive-info')}
          </h1>
        ) : (
          <h1 className="top-title-text">
            {t('start-test')}
            <br />
            {t('test-right-ear')} {!isMobile && <br />}
            {t('test-left-ear')}
          </h1>
        )}
      </div>

      <div
        className={
          !isTraining ? 'left-right-container' : 'left-right-container2'
        }
        style={{
          display: 'flex',
          justifyContent: frequency[0] === 'R' ? 'flex-end' : 'flex-start',
          maxWidth: '1000px',
          margin: '0 auto',
          backgroundColor: '#FFF',
        }}
      >
        <p>
          {frequency[0] === 'R' ? t('right-ear-label') : t('left-ear-label')} -{' '}
          {!isTraining
            ? indexMap[frequency.substring(1) as unknown as keyof IndexMap]
            : t('training')}
        </p>
      </div>

      <div className="main-content">
        {dataPosted ? (
          <>
            <p className="retest-text">
              {isRetest && (
                <>
                  {t('retest-time')} <strong>{getRetestTimeString()}</strong>
                </>
              )}
            </p>
            <button
              className="link-button"
              disabled={sequenceModeEnabled}
              onClick={() =>
                isRetest && !retestStarted
                  ? handleClickStartRetest()
                  : handleClickStartTest()
              }
            >
              {isRetest && !retestStarted
                ? t('start-retest')
                : freqList.length < 12
                  ? t('next-frequency-button')
                  : t('start-test-button')}
            </button>
            {sequenceModeEnabled && (
              <p className="testing-footer-text">
                {sequenceThreshold !== null
                  ? `Sequence validated! Threshold determined: ${sequenceThreshold} dB`
                  : 'Sequence validated! Threshold determined.'}
              </p>
            )}
          </>
        ) : testStarted ? (
          <>
            <p className="testing-text">
              {sequenceModeEnabled && selectedSequence
                ? `Playing ${loudness} dB`
                : t('testing-ear', {
                    ear: frequency[0] === 'R' ? t('right') : t('left'),
                  })}
            </p>
            <button
              id="heardButton"
              className={`
                link-button
                heard-button
                ${heardAnimation ? 'heard-animation' : ''}
              `}
              disabled={heardAnimation}
              onClick={() => handleClickHeard()}
            >
              <div />
              {t('heard-button')}
            </button>
            {sequenceModeEnabled && selectedSequence ? (
              <p
                className={`testing-footer-text sequence-cue ${
                  selectedSequence.expectedResponses[sequenceStepIndex]
                    ? 'sequence-cue-respond'
                    : 'sequence-cue-no-respond'
                }`}
              >
                {selectedSequence.expectedResponses[sequenceStepIndex]
                  ? 'Respond "Heard" now'
                  : 'do NOT respond'}
              </p>
            ) : (
              <p className="testing-footer-text">{t('normal-to-hear-nothing')}</p>
            )}
          </>
        ) : (
          <>
            <button
              className="link-button"
              onClick={() => setTestStarted(true)}
              disabled={sequenceModeEnabled}
            >
              {t('begin-button')}
            </button>
            <p className="testing-footer-text">
              {t('advance-automatically')}
              <br />
            </p>
          </>
        )}
        <img
          className="soundwaves"
          src={soundwaves}
          alt={t('sound-waves-alt')}
        />
      </div>

      {localHearingOnly && (
        <div className="local-hearing-only-banner">
          <p>
            REACT_APP_LOCAL_HEARING_ONLY=&quot;true&quot; --&gt; Dev mode
            feature to isolate the hearing test. No data will be{' '}
            <strong>stored</strong> or <strong>submitted</strong> to the API.
            Choose a test sequence from below (hover a test to preview):
          </p>
          {sequenceError && (
            <p className="sequence-error-message">{sequenceError}</p>
          )}
          <div className="sequence-options">
            {sequenceOptions.map((sequence) => {
              const bigJump = sequenceUsesBigMissJump(sequence.expectedResponses);
              return (
                <button
                  key={sequence.id}
                  className={`link-button sequence-option-button${
                    bigJump ? ' sequence-option-button--big-jump' : ''
                  }`}
                  title={sequence.sequence}
                  data-sequence={sequence.sequence}
                  onClick={() => startSequence(sequence)}
                  disabled={trialRunning || (testStarted && !dataPosted)}
                >
                  <span className="sequence-option-button-title">{sequence.id}</span>
                  {bigJump && (
                    <span className="sequence-option-big-jump-label">
                      Larger +5 jump
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!dataPosted && (
        <AudioPlayer
          loudness={loudness}
          frequency={frequency}
          autoPlay={trialRunning}
          isTestScreen
        />
      )}
    </div>
  );
};

export default Test;
